/**
 * Protected Route
 * Route guard for authenticated routes
 */

import type { UserType } from "@shared/types";
import { Navigate, useLocation } from "react-router-dom";
import { BottomNavigation } from "@/components/ui/bottom-navigation";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { AppLayout } from "./app-layout";

interface ProtectedRouteProps {
	children: React.ReactNode;
	requireUserType?: UserType | boolean;
	hideAppShell?: boolean;
}

export function ProtectedRoute({
	children,
	requireUserType = false,
	hideAppShell = false,
}: ProtectedRouteProps) {
	const location = useLocation();
	const {
		isAuthenticated,
		needsOnboarding,
		needsProfileSetup,
		needsPhoneCollection,
		getUserType,
	} = useAuthStore();

	const showAppShell = requireUserType !== false && !hideAppShell;

	// Where the visitor was trying to go, so the flows below can return them
	// there instead of dropping them on a dashboard. `redirectTo` is the shape
	// the public portfolio and contractor-profile pages already send to /login,
	// and that `verify-otp`, `google-signin-button` and `collect-phone` read on
	// the way out.
	const redirectTo = `${location.pathname}${location.search}${location.hash}`;

	// Not authenticated - redirect to login
	if (!isAuthenticated()) {
		return <Navigate to="/login" replace state={{ redirectTo }} />;
	}

	// Google user with no phone - redirect to collect-phone
	if (needsPhoneCollection() && location.pathname !== "/collect-phone") {
		return <Navigate to="/collect-phone" replace state={{ redirectTo }} />;
	}

	// Authenticated but needs onboarding (no user type chosen)
	if (needsOnboarding() && requireUserType) {
		return <Navigate to="/onboarding" replace state={{ redirectTo }} />;
	}

	// Authenticated, chosen userType, but needs profile setup / onboarding form completion
	if (needsProfileSetup() && requireUserType && !hideAppShell) {
		return (
			<Navigate
				to={`/onboarding/${getUserType()}`}
				replace
				state={{ redirectTo }}
			/>
		);
	}

	// Check specific user type requirement
	if (typeof requireUserType === "string") {
		const currentUserType = getUserType();
		if (currentUserType !== requireUserType) {
			// Redirect to dashboard if wrong user type. Deliberately carries no
			// `redirectTo`: nothing at /dashboard consumes one, and this visitor
			// is already signed in, so the type gate above would simply refuse
			// the same destination again.
			return <Navigate to="/dashboard" replace />;
		}
	}

	// Pages that require a user type get the full app shell (sidebar + bottom nav)
	// unless hideAppShell is true (e.g., onboarding pages)
	if (showAppShell) {
		return (
			<>
				<AppLayout>
					<div className="pb-14 md:pb-0">{children}</div>
				</AppLayout>
				<BottomNavigation />
			</>
		);
	}

	// Non-app-shell pages (onboarding, etc.)
	return <>{children}</>;
}
